import { useEffect, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import {
  mapPriceFormDataToInput,
  mapBundleFormData,
  mapDiscounts,
  transformPrice,
  PriceRangeType,
  getPriceChange,
  getPriceDisabled,
} from './editBundle.vm.mapper';
import { SubscriptionBundle } from '../../common/bundle.types';
import toast from 'react-hot-toast';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty, isEqual, isNil } from 'lodash';
import { BundlesModel } from '../model/subscriptionBundles.model';
import { PATHS } from '@client/routes';
import { trackEvent } from '@services/mixpanel';
import {
  BundleFormValues,
  formDefaultValues,
  priceFormSchema,
} from './priceFormSchemas';
import { browserHistory } from 'react-router';
import { getLinks } from '@client/new/pages/creator/home/mappers';
import { updateHash } from '../../common/helpers';
import { BundleItemType, BundleStatus } from 'generated/graphql';
import { ButtonColor } from '@design-system/components/button/button.css';
import {
  getCta,
  toNotPublishedProgramNames,
} from './editBundlePrice.vm.mappers';
import { ConfirmPublishModalProps } from '../../common/ConfirmPublishModal';

type PayloadMap = {
  editBundle: SubscriptionBundle;
  cancelEditBundle: null;
  savePriceForm: null;
  transformPrice: {
    value: string;
    type: PriceRangeType;
  };
  savePriceChange: null;
  cancelPriceChange: null;
  cancelPriceDisableChange: null;
  editLP: null;
  openLinksModal: null;
  closeLinksModal: null;
  createDiscount: null;
  publishBundle: null;
  confirmPublishBundle: null;
};

export type BundlesModelAction = Actions<PayloadMap>;
export type BundlesModelDispatcher = FSpread<PayloadMap>;

export type ModalCta = {
  label: string;
  onClick: () => void;
  color?: ButtonColor;
  submitting: boolean;
  disabled: boolean;
};

export type EditBundlePriceVM = {
  editBundlePrice: (id: SubscriptionBundle) => void;
  priceEditModal: {
    isOpen: boolean;
    close: () => void;
    freeTrial: {
      disabled: {
        monthly: boolean;
        annual: boolean;
      };
    };
    control: Control<BundleFormValues, any, BundleFormValues>;
    cta: Array<ModalCta>;
    error?: string;
    publishWarning: boolean;
    discounts: {
      count: number;
      createDiscount: () => void;
    };
    landingPage: {
      openLinksModal: () => void;
      disableLinksModal: boolean;
      editLP: () => void;
    };
    transformPrice: (value: string, type: PriceRangeType) => void;
  };
  confirmPriceModal: {
    isOpen: boolean;
    close: () => void;
    save: () => void;
    priceConfirm: {
      monthly?: string;
      annual?: string;
    };
  };
  confirmPriceDisableModal: {
    isOpen: boolean;
    close: () => void;
    save: () => void;
    disabledPrice: {
      monthly: boolean;
      annual: boolean;
    };
  };
  confirmPublishModal: ConfirmPublishModalProps;
  submittingBundleIds: number[];
  openLinksModal: () => void;
  linksModal: {
    isOpen: boolean;
    onClose: () => void;
    data: {
      links: Array<{
        title: string;
        url: string;
        description: string;
        onEdit?: () => void;
      }>;
    };
  };
};

export const useEditBundlePriceVM = ({
  model,
  creatorId,
}: {
  model: Async<BundlesModel>;
  creatorId: number;
}): EditBundlePriceVM => {
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [isSubmittingPrice, setSubmittingPrice] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishWarning, setPublishWarning] = useState(false);
  const [edittedBundle, setEdittedBundle] = useState<
    SubscriptionBundle | undefined
  >(undefined);
  const [confirmPriceChangeOpen, setConfirmPriceChangeOpen] =
    useState<boolean>(false);
  const [confirmPriceDisableOpen, setConfirmPriceDisableOpen] =
    useState<boolean>(false);
  const [confirmPublishDraftItems, setConfirmPublishDraftItems] = useState<
    string[]
  >([]);
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const {
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<BundleFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(priceFormSchema),
  });

  const modelValue = model.type === 'fetched' ? model.value : null;

  const annualPriceEnabled = watch('annualPriceEnabled');
  const monthlyPriceEnabled = watch('monthlyPriceEnabled');
  const annualPrice = watch('annualPrice');
  const monthlyPrice = watch('monthlyPrice');
  const annualFreeTrial = watch('annualFreeTrial');
  const monthlyFreeTrial = watch('monthlyFreeTrial');

  const originalPriceChangedTo = getPriceChange({
    monthlyPriceEnabled,
    monthlyPrice,
    annualPriceEnabled,
    annualPrice,
    bundlePrice: edittedBundle?.price,
  });

  const disabledPrice = getPriceDisabled({
    monthlyPriceEnabled,
    annualPriceEnabled,
    bundlePrice: edittedBundle?.price,
  });

  useEffect(() => {
    if (isFormInitialized) {
      trackEvent({
        type: 'Subscription price state toggled',
        payload: {
          type: annualPriceEnabled ? 'annual' : 'monthly',
          offerId: edittedBundle?.id || 0,
        },
      });
    }
  }, [annualPriceEnabled, monthlyPriceEnabled, isFormInitialized]);

  useEffect(() => {
    if (isFormInitialized) {
      trackEvent({
        type: '7 day free trial changed',
      });
    }
  }, [annualFreeTrial, monthlyFreeTrial, isFormInitialized]);

  const editBundle = (bundle: SubscriptionBundle) => {
    setEdittedBundle(bundle);
    setBundleForm(bundle);
    setIsFormInitialized(true);
    setPublishWarning(false);
    updateHash(`#${bundle.id}`);
  };

  const setBundleForm = (bundle: SubscriptionBundle) => {
    const values = mapBundleFormData(bundle);
    reset(values, {
      keepValues: false,
      keepDirty: false,
      keepDirtyValues: false,
    });
  };

  const executePublish = () => {
    if (isNil(edittedBundle) || isNil(modelValue)) {
      toast.error('Could not save offer update. Please refresh data');
      return;
    }

    const hasUnsavedChanges = !isEqual(mapBundleFormData(edittedBundle), {
      monthlyPriceEnabled,
      annualPriceEnabled,
      monthlyPrice,
      annualPrice,
      monthlyFreeTrial,
      annualFreeTrial,
    });

    if (hasUnsavedChanges) {
      setPublishWarning(true);
      return;
    }

    setPublishWarning(false);
    setIsPublishing(true);

    modelValue
      .publishBundle(edittedBundle.id)
      .then(() => {
        toast.success('Subscription offer was published successfully');
        trackEvent({
          type: 'Exclusive Subscription Published',
          payload: {
            offerId: edittedBundle.id,
          },
        });
      })
      .catch((e) => {
        toast.error(e.message);
      })
      .finally(() => {
        modelValue.refetch().finally(() => {
          setIsPublishing(false);
        });
      });
  };

  const publishBundle = () => {
    if (isNil(edittedBundle) || isNil(modelValue)) {
      executePublish();
      return;
    }

    const programIds = edittedBundle.items
      .filter((i) => i.itemType === BundleItemType.Program)
      .map((i) => i.itemId)
      .filter((id): id is number => !isNil(id));

    const notPublishedPrograms = toNotPublishedProgramNames({
      bundleProgramIds: programIds,
      subscriptionPrograms: modelValue.subscriptionPrograms,
      allSubscriptionBundles: modelValue.initialSubscriptionBundles,
    });

    const contentIds = edittedBundle.items
      .filter((i) => i.itemType === BundleItemType.Content)
      .map((i) => i.contentId)
      .filter((id): id is string => !isNil(id));

    const notPublishedContent = modelValue.sellableContent
      .filter((c) => contentIds.includes(c.id))
      .filter((c) => !c.isPublished)
      .map((c) => c.name);

    const draftItems = [...notPublishedPrograms, ...notPublishedContent];

    if (isEmpty(draftItems)) {
      executePublish();
      return;
    }

    setConfirmPublishDraftItems(draftItems);
  };

  const savePriceForm = handleSubmit(
    (values) => {
      if (isNil(edittedBundle)) {
        toast.error('Could not save offer update. Please refresh data');
        return;
      }
      const input = mapPriceFormDataToInput(edittedBundle, values);
      modelValue &&
        modelValue
          .updateBundlePrices(input)
          .then(() => {
            toast.success('Subscription offers were updated successfully');

            if (
              originalPriceChangedTo.annual ||
              originalPriceChangedTo.monthly
            ) {
              trackEvent({
                type: 'Subscription price changed',
                payload: {
                  type: originalPriceChangedTo.annual ? 'annual' : 'monthly',
                  offerId: edittedBundle?.id || 0,
                },
              });
            }

            if (
              !originalPriceChangedTo.annual ||
              !originalPriceChangedTo.monthly
            ) {
              trackEvent({
                type: 'Subscription price saved',
                payload: {
                  offerId: edittedBundle?.id || 0,
                },
              });
            }
          })
          .catch((e) => {
            toast.error(e.message);
          })
          .finally(() => {
            modelValue.refetch().finally(() => {
              setSubmittingPrice(false);
              setEdittedBundle(undefined);
              setIsFormInitialized(false);
              modelValue.refetchInfluencer();
            });
          });
    },
    () => {
      setSubmittingPrice(false);
    },
  );

  const dispatch: BundlesModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as BundlesModelAction;
    switch (action.type) {
      case 'editBundle': {
        editBundle(action.payload);
        return null;
      }
      case 'cancelEditBundle':
        {
          setEdittedBundle(undefined);
          reset(formDefaultValues, {
            keepValues: false,
            keepDirty: false,
            keepDirtyValues: false,
          });
          setIsFormInitialized(false);
          setPublishWarning(false);
          updateHash(window.location.pathname);
        }
        return null;
      case 'savePriceForm':
        setSubmittingPrice(true);
        setConfirmPriceChangeOpen(false);
        setConfirmPriceDisableOpen(false);
        savePriceForm();
        return null;
      case 'transformPrice':
        transformPrice(action.payload.value, action.payload.type, setValue);
        return null;
      case 'savePriceChange': {
        if (originalPriceChangedTo.annual || originalPriceChangedTo.monthly) {
          setConfirmPriceChangeOpen(true);
          return null;
        }
        if (disabledPrice.annual || disabledPrice.monthly) {
          setConfirmPriceDisableOpen(true);
          return null;
        }

        savePriceForm();
        return null;
      }
      case 'cancelPriceChange':
        setConfirmPriceChangeOpen(false);
        return null;
      case 'cancelPriceDisableChange':
        setConfirmPriceDisableOpen(false);
        return null;
      case 'editLP':
        browserHistory.push(PATHS.LANDING_PAGE(creatorId));
        return null;
      case 'openLinksModal':
        setShowLinksModal(true);
        return null;
      case 'closeLinksModal':
        setShowLinksModal(false);
        return null;
      case 'createDiscount':
        browserHistory.push(
          PATHS.PROMOTIONS(creatorId, {
            mode: 'create',
          }),
        );
        return null;
      case 'publishBundle':
        publishBundle();
        return null;
      case 'confirmPublishBundle':
        setConfirmPublishDraftItems([]);
        executePublish();
        return null;
    }
  };

  const isDraftMode = Boolean(
    edittedBundle && edittedBundle.status === BundleStatus.Draft,
  );

  return {
    editBundlePrice: (bundle) => dispatch('editBundle', bundle),
    priceEditModal: {
      isOpen: !!edittedBundle,
      close: () => dispatch('cancelEditBundle'),
      freeTrial: {
        disabled: {
          monthly: !monthlyPriceEnabled,
          annual: !annualPriceEnabled,
        },
      },
      control: control,
      cta: getCta(dispatch, isDraftMode, isSubmittingPrice, isPublishing),
      error: errors.monthlyPriceEnabled?.message,
      publishWarning,
      discounts: {
        count: mapDiscounts({
          editBundle: edittedBundle,
          discounts: modelValue?.discounts,
        }),
        createDiscount: () => dispatch('createDiscount'),
      },
      landingPage: {
        openLinksModal: () => dispatch('openLinksModal'),
        disableLinksModal: isDraftMode,
        editLP: () => dispatch('editLP'),
      },
      transformPrice: (value: string, type: PriceRangeType) =>
        dispatch('transformPrice', { value, type }),
    },
    confirmPriceModal: {
      isOpen: confirmPriceChangeOpen,
      close: () => dispatch('cancelPriceChange'),
      save: () => dispatch('savePriceForm'),
      priceConfirm: originalPriceChangedTo,
    },
    confirmPriceDisableModal: {
      isOpen: confirmPriceDisableOpen,
      close: () => dispatch('cancelPriceDisableChange'),
      save: () => dispatch('savePriceForm'),
      disabledPrice,
    },
    confirmPublishModal: {
      isOpen: !isEmpty(confirmPublishDraftItems),
      confirm: () => dispatch('confirmPublishBundle'),
      cancel: () => setConfirmPublishDraftItems([]),
      draftItems: confirmPublishDraftItems,
    },
    submittingBundleIds:
      isSubmittingPrice && edittedBundle?.id ? [edittedBundle.id] : [],
    openLinksModal: () => dispatch('openLinksModal'),
    linksModal: {
      isOpen: showLinksModal,
      onClose: () => dispatch('closeLinksModal'),
      data: {
        links: getLinks(
          'subscription',
          true,
          modelValue?.customURL,
          undefined,
          () => dispatch('editLP'),
        ),
      },
    },
  };
};
